import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type {
  AccountStatusOutput,
  RequestDeletionInput,
  DeletionRequestOutput,
  CancelDeletionOutput,
  DpaCurrentOutput,
  DpaAcceptInput,
  DpaAcceptOutput,
} from '@shared/validators/account.schema';

/*
 * Account- ja DPA-data-kerros (Sessio 15, GDPR-B).
 *
 * Hookit:
 *  - useAccountQuery -> GET /api/me/account. Tilin tila + DPA-version.
 *  - useRequestDeletionMutation -> POST /api/me/account/delete (pwd + confirm + MFA).
 *  - useCancelDeletionMutation -> POST /api/me/account/delete/cancel.
 *  - useDpaCurrentQuery -> GET /api/me/dpa/current.
 *  - useAcceptDpaMutation -> POST /api/me/dpa/accept.
 *
 * Mutaatiot invalidoivat account-queryn jotta UI paivittyy automaattisesti.
 */

const QUERY_KEYS = {
  account: ['me', 'account'] as const,
  dpaCurrent: ['dpa', 'current'] as const,
};

const ACCOUNT_STALE_MS = 30 * 1000;
const DPA_STALE_MS = 5 * 60 * 1000;

export function useAccountQuery() {
  return useQuery<AccountStatusOutput>({
    queryKey: QUERY_KEYS.account,
    queryFn: ({ signal }) => apiFetch<AccountStatusOutput>('/api/me/account', { signal }),
    staleTime: ACCOUNT_STALE_MS,
  });
}

export function useRequestDeletionMutation() {
  const queryClient = useQueryClient();
  return useMutation<DeletionRequestOutput, Error, RequestDeletionInput>({
    mutationFn: (input) =>
      apiFetch<DeletionRequestOutput>('/api/me/account/delete', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.account });
    },
  });
}

export function useCancelDeletionMutation() {
  const queryClient = useQueryClient();
  return useMutation<CancelDeletionOutput, Error, void>({
    mutationFn: () =>
      apiFetch<CancelDeletionOutput>('/api/me/account/delete/cancel', {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.account });
    },
  });
}

export function useDpaCurrentQuery() {
  return useQuery<DpaCurrentOutput>({
    queryKey: QUERY_KEYS.dpaCurrent,
    queryFn: ({ signal }) => apiFetch<DpaCurrentOutput>('/api/me/dpa/current', { signal }),
    staleTime: DPA_STALE_MS,
  });
}

export function useAcceptDpaMutation() {
  const queryClient = useQueryClient();
  return useMutation<DpaAcceptOutput, Error, DpaAcceptInput>({
    mutationFn: (input) =>
      apiFetch<DpaAcceptOutput>('/api/me/dpa/accept', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.account });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dpaCurrent });
    },
  });
}
